<script lang="ts">
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Button from "../../components/input/Button.svelte";
  import CategoryIcon from "../../components/data-display/CategoryIcon.svelte";
  import ConfirmDialog from "../../components/overlay/ConfirmDialog.svelte";
  import EmptyState from "../../components/feedback/EmptyState.svelte";
  import { formatCurrency } from "../../lib/utils/format";

  type ShoppingItem = {
    id: string;
    name: string;
    category?: string;
    quantity: number;
    price_cents?: number;
    currency?: string;
    store_name?: string;
    purchased: boolean;
    purchased_at?: string;
  };

  const CATEGORIES = ["전체", "식품", "의류", "기념품", "뷰티", "전자기기", "기타"];

  let { trip, items = [] }: { trip: any; items?: ShoppingItem[] } = $props();

  let localItems = $state<ShoppingItem[]>([]);
  let showForm = $state(false);
  let submitting = $state(false);
  let categoryFilter = $state("전체");
  let confirmDeleteId = $state<string | null>(null);
  let confirmOpen = $state(false);
  let itemsHydrated = false;
  let form = $state({ name: "", category: "", quantity: 1, price_cents: "", store_name: "" });

  const pendingItems = $derived(
    localItems.filter((i) => !i.purchased && (categoryFilter === "전체" || i.category === categoryFilter))
  );
  const purchasedItems = $derived(localItems.filter((i) => i.purchased));

  const totalPending = $derived(
    localItems.filter((i) => !i.purchased).reduce((sum, i) => sum + (i.price_cents ?? 0) * i.quantity, 0)
  );
  const totalPurchased = $derived(
    localItems.filter((i) => i.purchased).reduce((sum, i) => sum + (i.price_cents ?? 0) * i.quantity, 0)
  );

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  $effect(() => {
    if (itemsHydrated) return;
    localItems = [...items];
    itemsHydrated = true;
  });

  async function createItem() {
    if (!form.name.trim()) return;
    submitting = true;
    const res = await fetch(`/api/v1/trips/${trip.id}/shopping_items`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({
        shopping_item: {
          name: form.name,
          category: form.category || null,
          quantity: Number(form.quantity) || 1,
          price_cents: form.price_cents ? Number(form.price_cents) : null,
          store_name: form.store_name || null,
        },
      }),
    });
    submitting = false;
    if (!res.ok) return;
    const json = await res.json();
    const newItem = json.data || json;
    localItems = [...localItems, newItem];
    showForm = false;
    form = { name: "", category: "", quantity: 1, price_cents: "", store_name: "" };
  }

  async function togglePurchased(itemId: string) {
    const res = await fetch(`/api/v1/trips/${trip.id}/shopping_items/${itemId}/toggle_purchased`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
    });
    if (!res.ok) return;
    localItems = localItems.map((i) =>
      i.id === itemId ? { ...i, purchased: !i.purchased, purchased_at: i.purchased ? undefined : new Date().toISOString() } : i
    );
  }

  function askDelete(id: string) {
    confirmDeleteId = id;
    confirmOpen = true;
  }

  async function removeItem() {
    if (!confirmDeleteId) return;
    await fetch(`/api/v1/trips/${trip.id}/shopping_items/${confirmDeleteId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken() },
    });
    localItems = localItems.filter((i) => i.id !== confirmDeleteId);
    confirmDeleteId = null;
  }
</script>

<TripSubNav {trip} currentPage="shopping" />

<div class="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
  <!-- Header -->
  <div class="flex items-center justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold text-primary">쇼핑 리스트</h1>
      <p class="mt-1 text-sm text-muted">구매 예정과 완료 항목을 함께 관리합니다.</p>
    </div>
    <Button variant="primary" size="sm" onclick={() => (showForm = true)}>+ 아이템 추가</Button>
  </div>

  <!-- Summary stats -->
  <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
    <div class="rounded-xl border border-white/8 bg-white/5 p-3 text-center">
      <p class="text-xl font-bold text-primary">{localItems.filter((i) => !i.purchased).length}</p>
      <p class="text-xs text-muted mt-0.5">미구매</p>
    </div>
    <div class="rounded-xl border border-white/8 bg-white/5 p-3 text-center">
      <p class="text-xl font-bold text-teal-400">{localItems.filter((i) => i.purchased).length}</p>
      <p class="text-xs text-muted mt-0.5">구매 완료</p>
    </div>
    <div class="rounded-xl border border-white/8 bg-white/5 p-3 text-center">
      <p class="text-base font-bold text-primary truncate">{totalPending > 0 ? formatCurrency(totalPending) : "-"}</p>
      <p class="text-xs text-muted mt-0.5">예상 금액</p>
    </div>
    <div class="rounded-xl border border-white/8 bg-white/5 p-3 text-center">
      <p class="text-base font-bold text-green-400 truncate">{totalPurchased > 0 ? formatCurrency(totalPurchased) : "-"}</p>
      <p class="text-xs text-muted mt-0.5">지출 완료</p>
    </div>
  </div>

  <!-- Category filter -->
  <div class="flex gap-2 overflow-x-auto pb-1">
    {#each CATEGORIES as cat}
      <button
        type="button"
        onclick={() => (categoryFilter = cat)}
        class="shrink-0 rounded-xl px-3 py-1.5 text-sm font-medium transition-colors
          {categoryFilter === cat ? 'bg-teal-400/15 text-teal-400 border border-teal-400/30' : 'border border-white/10 text-muted hover:text-secondary'}"
      >
        {cat}
      </button>
    {/each}
  </div>

  <!-- Pending -->
  <section class="space-y-2">
    <h2 class="text-sm font-semibold uppercase tracking-wider text-muted">구매 예정 ({pendingItems.length})</h2>
    {#if pendingItems.length === 0}
      <EmptyState icon="🛍️" title="목록 비어 있음" description="구매할 아이템을 추가해보세요." />
    {:else}
      {#each pendingItems as item (item.id)}
        <article class="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/60 p-3.5">
          <button
            type="button"
            aria-label="구매 완료"
            class="shrink-0 h-5 w-5 rounded-full border-2 border-teal-400/50 hover:bg-teal-400/20 transition-colors"
            onclick={() => togglePurchased(item.id)}
          ></button>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              {#if item.category}
                <CategoryIcon category={item.category} size={14} />
              {/if}
              <p class="font-medium text-primary">{item.name}</p>
              {#if item.category}
                <span class="rounded-full bg-white/8 px-2 py-0.5 text-xs text-muted">{item.category}</span>
              {/if}
            </div>
            <p class="mt-0.5 text-xs text-muted">
              수량 {item.quantity}{item.store_name ? ` · ${item.store_name}` : ""}
            </p>
          </div>
          <div class="shrink-0 flex items-center gap-3">
            {#if item.price_cents}
              <span class="text-sm text-muted">{formatCurrency(item.price_cents * item.quantity, item.currency || "KRW")}</span>
            {/if}
            <button type="button" class="text-xs text-rose-400 hover:text-rose-300" onclick={() => askDelete(item.id)}>삭제</button>
          </div>
        </article>
      {/each}
    {/if}
  </section>

  <!-- Purchased -->
  {#if purchasedItems.length > 0}
    <section class="space-y-2">
      <h2 class="text-sm font-semibold uppercase tracking-wider text-muted">구매 완료 ({purchasedItems.length})</h2>
      {#each purchasedItems as item (item.id)}
        <article class="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/3 p-3.5 opacity-60">
          <button
            type="button"
            aria-label="구매 취소"
            class="shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-teal-400 text-xs text-slate-950"
            onclick={() => togglePurchased(item.id)}
          >✓</button>
          <div class="min-w-0 flex-1">
            <p class="font-medium text-muted line-through">{item.name}</p>
            <p class="text-xs text-secondary">{item.purchased_at ? new Date(item.purchased_at).toLocaleDateString("ko-KR") : "완료"}</p>
          </div>
          {#if item.price_cents}
            <span class="shrink-0 text-sm text-muted">{formatCurrency(item.price_cents * item.quantity, item.currency || "KRW")}</span>
          {/if}
        </article>
      {/each}
    </section>
  {/if}
</div>

<!-- Add Modal -->
{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
    <div class="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950 p-6">
      <div class="mb-5 flex items-center justify-between">
        <h2 class="text-lg font-semibold">아이템 추가</h2>
        <button type="button" class="text-muted hover:text-primary" onclick={() => (showForm = false)}>✕</button>
      </div>
      <div class="space-y-3">
        <input bind:value={form.name} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary placeholder-gray-500 outline-none focus:border-teal-400" placeholder="상품명 *" />
        <div>
          <span class="text-xs text-muted mb-1 block">카테고리</span>
          <select bind:value={form.category} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary">
            <option value="">선택 안함</option>
            {#each CATEGORIES.slice(1) as cat}
              <option value={cat}>{cat}</option>
            {/each}
          </select>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <span class="text-xs text-muted mb-1 block">수량</span>
            <input bind:value={form.quantity} type="number" min="1" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary outline-none focus:border-teal-400" />
          </div>
          <div>
            <span class="text-xs text-muted mb-1 block">가격 (원)</span>
            <input bind:value={form.price_cents} type="number" min="0" class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary placeholder-gray-500 outline-none focus:border-teal-400" placeholder="0" />
          </div>
        </div>
        <input bind:value={form.store_name} class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary placeholder-gray-500 outline-none focus:border-teal-400" placeholder="구매처 (선택)" />
        <Button variant="primary" fullWidth loading={submitting} onclick={createItem}>저장</Button>
      </div>
    </div>
  </div>
{/if}

<ConfirmDialog
  bind:open={confirmOpen}
  title="아이템 삭제"
  message="이 아이템을 삭제하시겠습니까?"
  confirmLabel="삭제"
  onconfirm={removeItem}
/>
